import { computed, onMounted, ref, unref, watch, type MaybeRef } from 'vue'
import { useRoute } from 'vue-router'
import {
  readGridDetailRow,
  resolveDetailRowFromQuery,
} from '../utils/detail-row-store'

type RouteLike = { query: Record<string, unknown> }

export interface UseGridDetailRowCommonOptions {
  fallback?: () =>
    | Promise<Record<string, unknown> | null>
    | Record<string, unknown>
    | null
}

export interface UseGridDetailRowManualOptions
  extends UseGridDetailRowCommonOptions {
  module: MaybeRef<string>
  id: MaybeRef<string | number | null | undefined>
}

export interface UseGridDetailRowRouteOptions
  extends UseGridDetailRowCommonOptions {
  route: MaybeRef<RouteLike>
}

export type UseGridDetailRowOptions =
  | UseGridDetailRowManualOptions
  | UseGridDetailRowRouteOptions
  | UseGridDetailRowCommonOptions

function isManualOptions(
  options: UseGridDetailRowOptions
): options is UseGridDetailRowManualOptions {
  return 'module' in options && 'id' in options
}

function isRouteOptions(
  options: UseGridDetailRowOptions
): options is UseGridDetailRowRouteOptions {
  return 'route' in options
}

export function useGridDetailRow(options?: UseGridDetailRowOptions) {
  const fallback = options?.fallback
  let module: MaybeRef<string>
  let id: MaybeRef<string | number | null | undefined>

  if (options && isManualOptions(options)) {
    module = options.module
    id = options.id
  } else if (options && isRouteOptions(options)) {
    module = computed(
      () => resolveDetailRowFromQuery(unref(options.route).query).module
    )
    id = computed(
      () => resolveDetailRowFromQuery(unref(options.route).query).id
    )
  } else {
    const route = useRoute()
    module = computed(() => resolveDetailRowFromQuery(route.query).module)
    id = computed(() => resolveDetailRowFromQuery(route.query).id)
  }

  const row = ref<Record<string, unknown> | null>(null)

  async function reload() {
    const moduleValue = unref(module)
    const idValue = unref(id)
    row.value = readGridDetailRow(moduleValue, idValue)
    if (!row.value && fallback) {
      row.value = (await fallback()) ?? null
    }
  }

  onMounted(() => {
    void reload()
  })

  watch(
    () => [unref(module), unref(id)] as const,
    () => {
      void reload()
    }
  )

  return { row, reload }
}
